import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, X } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { api, fmtMoney, todayISO, addDaysISO } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

export default function MyBookingsPage() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [bookings, setBookings] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [modifying, setModifying] = useState(null);

  function reload() {
    api.get("/bookings/mine").then((r) => setBookings(r.data)).finally(() => setFetching(false));
  }
  useEffect(() => { if (user) reload(); }, [user]);

  async function cancel(id) {
    if (!confirm("Cancel this booking?")) return;
    try {
      await api.post(`/bookings/${id}/cancel`);
      setBookings((bs) => bs.map((b) => b.id === id ? { ...b, status: "cancelled" } : b));
      toast.success("Booking cancelled");
    } catch {
      toast.error("Could not cancel");
    }
  }

  if (loading) return <SiteLayout><div className="py-24 text-center">Loading…</div></SiteLayout>;
  if (!user) return (
    <SiteLayout>
      <div className="max-w-md mx-auto py-24 text-center">
        <h1 className="font-display text-4xl">{t("auth.signin")}</h1>
        <Link to="/login" className="inline-block mt-6 bg-brand-primary text-white px-6 py-3 rounded-sm text-sm">{t("auth.signin")}</Link>
      </div>
    </SiteLayout>
  );

  return (
    <SiteLayout>
      <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 py-16">
        <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("mybookings.welcome")}, {user.name}</div>
        <h1 className="font-display text-5xl mt-3">{t("mybookings.title")}</h1>

        {fetching ? <div className="py-12 text-center text-brand-muted">Loading…</div> :
          bookings.length === 0 ? (
            <div className="mt-16 text-center" data-testid="my-bookings-empty">
              <p className="font-display text-3xl">{t("mybookings.empty")}</p>
              <Link to="/rooms" className="inline-block mt-6 bg-brand-primary text-white px-6 py-3 rounded-sm text-sm">{t("mybookings.find")}</Link>
            </div>
          ) : (
            <div className="mt-12 space-y-4" data-testid="my-bookings-list">
              {bookings.map((b) => <BookingRow key={b.id} b={b} onCancel={cancel} onModify={setModifying} t={t} />)}
            </div>
          )}
      </div>
      {modifying && <ModifyModal booking={modifying} onClose={() => setModifying(null)} onUpdated={() => { setModifying(null); reload(); }} />}
    </SiteLayout>
  );
}

function BookingRow({ b, onCancel, onModify, t }) {
  const statusColors = {
    confirmed: "bg-brand-success/10 text-brand-success",
    pending: "bg-brand-warning/20 text-brand-warning",
    cancelled: "bg-brand-error/10 text-brand-error",
  };
  return (
    <div data-testid={`booking-row-${b.id}`} className="bg-white border border-brand-border rounded-sm p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
      <div className="flex-1">
        <div className="font-display text-2xl">{b.room_name}</div>
        <div className="text-sm text-brand-muted mt-1">{b.check_in} → {b.check_out} · {b.nights} nights · {b.guests} {b.guests > 1 ? "guests" : "guest"}</div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <span className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded-sm ${statusColors[b.status] || "bg-gray-100 text-gray-600"}`}>{b.status}</span>
          <span className="text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded-sm bg-gray-100 text-gray-600">{b.payment_status}</span>
          {b.status !== "cancelled" && (
            <button onClick={() => onModify(b)} data-testid={`modify-booking-${b.id}`} className="text-xs text-brand-primary hover:text-brand-primaryHover flex items-center gap-1">
              <Pencil size={12} /> Change dates / guests
            </button>
          )}
          {b.status === "confirmed" && (
            <Link to="/reviews" className="text-xs text-brand-primary hover:text-brand-primaryHover">{t("mybookings.write_review")} →</Link>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className="font-display text-2xl text-brand-primary">{fmtMoney(b.total)}</div>
        {b.status !== "cancelled" && (
          <button onClick={() => onCancel(b.id)} className="mt-2 text-xs text-brand-error hover:underline" data-testid={`cancel-booking-${b.id}`}>{t("mybookings.cancel")}</button>
        )}
      </div>
    </div>
  );
}

function ModifyModal({ booking, onClose, onUpdated }) {
  const [checkIn, setCheckIn] = useState(booking.check_in);
  const [checkOut, setCheckOut] = useState(booking.check_out);
  const [guests, setGuests] = useState(booking.guests);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post(`/bookings/${booking.id}/modify`, {
        check_in: checkIn, check_out: checkOut, guests,
        origin_url: window.location.origin,
      });
      if (r.data.status === "payment_required") {
        window.location.href = r.data.checkout_url;
      } else {
        toast.success(`Booking updated${r.data.diff < 0 ? ` — credit of €${Math.abs(r.data.diff)} on file` : ""}`);
        onUpdated();
      }
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not modify");
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="modify-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-md rounded-sm">
        <div className="p-5 border-b flex justify-between items-center">
          <h2 className="font-display text-2xl">Change your stay</h2>
          <button type="button" onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-brand-muted">If the new dates/guests cost more, you'll be redirected to pay the difference. If less, a credit is held on your booking.</p>
          <Field label="Check-in">
            <input data-testid="modify-checkin" type="date" min={todayISO()} value={checkIn} onChange={(e) => {
              setCheckIn(e.target.value);
              if (e.target.value >= checkOut) setCheckOut(addDaysISO(e.target.value, 1));
            }} className="berdhill-mi" />
          </Field>
          <Field label="Check-out">
            <input data-testid="modify-checkout" type="date" min={addDaysISO(checkIn, 1)} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className="berdhill-mi" />
          </Field>
          <Field label="Guests">
            <input data-testid="modify-guests" type="number" min={1} value={guests} onChange={(e) => setGuests(Number(e.target.value))} className="berdhill-mi" />
          </Field>
          <style>{`.berdhill-mi{width:100%;border:1px solid #E6E2DB;border-radius:.125rem;padding:.5rem .75rem;font-size:.875rem;background:#fff}`}</style>
        </div>
        <div className="p-5 border-t flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm">Cancel</button>
          <button data-testid="modify-submit" disabled={submitting} type="submit" className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm disabled:opacity-50">
            {submitting ? "…" : "Apply changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-brand-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
